import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase.js';
import { branding } from './siteConfig.js';

const MAX_MESSAGE_CHARS = 1200;
const MAX_HISTORY_TURNS = 6;

const OWNER_SUGGESTIONS = [
  'Why are you called Nanny?',
  'Tell me about Nantahala',
  'What does the flight radar page do?',
  'Open the gate for me',
];

const ADMIN_SUGGESTIONS = [
  'What does the Watcher panel do?',
  'How do I refresh the news feed?',
  'Where do I manage Ring mailbox setup?',
  'Open the gate now',
];

function speechSupported() {
  return typeof window !== 'undefined'
    && ('speechSynthesis' in window || 'webkitSpeechSynthesis' in window);
}

function recognitionSupported() {
  return typeof window !== 'undefined'
    && (window.SpeechRecognition || window.webkitSpeechRecognition);
}

function speakText(text, onEnd) {
  if (!speechSupported() || !text?.trim()) {
    onEnd?.();
    return;
  }
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text.replace(/\n+/g, '. '));
  utterance.rate = 1;
  utterance.pitch = 1;
  utterance.onend = () => onEnd?.();
  utterance.onerror = () => onEnd?.();
  window.speechSynthesis.speak(utterance);
}

function NannyAssistant({ user, isAdmin }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [voiceMode, setVoiceMode] = useState(false);
  const [listening, setListening] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const fabRef = useRef(null);
  const recognitionRef = useRef(null);

  const suggestions = isAdmin ? ADMIN_SUGGESTIONS : OWNER_SUGGESTIONS;
  const canUseVoice = speechSupported() || recognitionSupported();

  const closePanel = useCallback(() => {
    setOpen(false);
    window.speechSynthesis?.cancel();
    recognitionRef.current?.stop?.();
    setListening(false);
    fabRef.current?.focus();
  }, []);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') closePanel();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [closePanel, open]);

  const sendMessage = useCallback(async (text) => {
    const trimmed = text.trim().slice(0, MAX_MESSAGE_CHARS);
    if (!trimmed || loading) return;

    setError('');
    setInput('');
    const nextMessages = [...messages, { role: 'user', content: trimmed }];
    setMessages(nextMessages);
    setLoading(true);

    try {
      const fn = httpsCallable(functions, 'nannyAssistantFn');
      const history = nextMessages.slice(-MAX_HISTORY_TURNS * 2, -1);
      const result = await fn({
        message: trimmed,
        history,
        currentPath: location.pathname,
        communityName: branding.communityName,
      });

      const navPath = result.data?.navigation?.path;
      let reply = result.data?.reply || 'Sorry, I could not answer that.';
      if (navPath) {
        navigate(navPath);
        reply = `${reply}\n\n→ Opening ${navPath} for you.`;
      }
      setMessages((prev) => [...prev, { role: 'assistant', content: reply }]);

      if (!result.data?.configured) {
        setError('AI is offline — Nanny is using fallback guidance. Set XAI_API_KEY on Cloud Functions for full answers.');
      }

      if (voiceMode) {
        speakText(reply);
      }
    } catch (err) {
      setError(err.message || 'Could not reach Nanny');
      setMessages((prev) => prev.slice(0, -1));
      setInput(trimmed);
    } finally {
      setLoading(false);
    }
  }, [loading, location.pathname, messages, navigate, voiceMode]);

  const startListening = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setError('Voice input is not supported in this browser. Try Chrome or Edge.');
      return;
    }
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onstart = () => setListening(true);
    recognition.onend = () => setListening(false);
    recognition.onerror = () => setListening(false);
    recognition.onresult = (event) => {
      const transcript = event.results?.[0]?.[0]?.transcript;
      if (transcript) {
        setInput(transcript);
        void sendMessage(transcript);
      }
    };
    recognitionRef.current = recognition;
    recognition.start();
  }, [sendMessage]);

  const stopListening = useCallback(() => {
    recognitionRef.current?.stop();
    setListening(false);
  }, []);

  if (!user) return null;

  const ui = (
    <div className={`nanny-assistant${open ? ' nanny-assistant--open' : ''}`}>
      {open ? (
        <button
          type="button"
          className="nanny-assistant-backdrop"
          aria-label="Close Nanny"
          onClick={closePanel}
        />
      ) : null}

      {open ? (
        <section
          id="nanny-assistant-panel"
          className="nanny-assistant-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="nanny-assistant-title"
        >
          <header className="nanny-assistant-header">
            <div>
              <h2 id="nanny-assistant-title" className="nanny-assistant-title">Nanny</h2>
              <p className="nanny-assistant-subtitle">
                {isAdmin ? 'Admin help' : 'Owner help'} · {branding.communityName}
                {canUseVoice ? ' · voice enabled' : ''}
              </p>
            </div>
            <div className="nanny-assistant-header-actions">
              {canUseVoice ? (
                <button
                  type="button"
                  className={`nanny-assistant-icon-btn${voiceMode ? ' nanny-assistant-icon-btn--active' : ''}`}
                  onClick={() => setVoiceMode((v) => !v)}
                  aria-pressed={voiceMode}
                  title="Read replies aloud"
                >
                  🔊
                </button>
              ) : null}
              <button
                type="button"
                className="nanny-assistant-close"
                onClick={closePanel}
                aria-label="Close Nanny"
              >
                ×
              </button>
            </div>
          </header>

          <div className="nanny-assistant-messages" ref={scrollRef} aria-live="polite">
            {messages.length === 0 ? (
              <div className="nanny-assistant-empty">
                <p>
                  Hi! I&apos;m Nanny — named for the Nantahala River in our mountains. Ask about portal features,
                  Nantahala history, or say &quot;take me to…&quot; any page. I can open the gate too.
                </p>
                <div className="nanny-assistant-suggestions">
                  {suggestions.map((q) => (
                    <button key={q} type="button" className="nanny-assistant-chip" onClick={() => void sendMessage(q)}>
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((msg, index) => (
                <div key={`${msg.role}-${index}`} className={`nanny-assistant-bubble nanny-assistant-bubble--${msg.role}`}>
                  <div className="nanny-assistant-bubble-text">{msg.content}</div>
                  {msg.role === 'assistant' && speechSupported() ? (
                    <button
                      type="button"
                      className="nanny-assistant-speak-btn"
                      onClick={() => speakText(msg.content)}
                      aria-label="Read aloud"
                    >
                      Listen
                    </button>
                  ) : null}
                </div>
              ))
            )}
            {loading ? <p className="nanny-assistant-typing">Nanny is thinking…</p> : null}
          </div>

          {error ? <p className="nanny-assistant-error" role="alert">{error}</p> : null}

          <form
            className="nanny-assistant-form"
            onSubmit={(e) => {
              e.preventDefault();
              void sendMessage(input);
            }}
          >
            {recognitionSupported() ? (
              <button
                type="button"
                className={`nanny-assistant-mic${listening ? ' nanny-assistant-mic--active' : ''}`}
                onClick={listening ? stopListening : startListening}
                disabled={loading}
                aria-label={listening ? 'Stop listening' : 'Speak your question'}
                title={listening ? 'Stop listening' : 'Speak your question'}
              >
                {listening ? '■' : '🎤'}
              </button>
            ) : null}
            <textarea
              ref={inputRef}
              className="nanny-assistant-input"
              rows={2}
              placeholder="Ask Nanny about the portal…"
              value={input}
              maxLength={MAX_MESSAGE_CHARS}
              onChange={(e) => setInput(e.target.value)}
              disabled={loading}
              aria-label="Message to Nanny"
            />
            <button type="submit" className="nanny-assistant-send" disabled={loading || !input.trim()}>
              Send
            </button>
          </form>
        </section>
      ) : null}

      <button
        ref={fabRef}
        type="button"
        className="nanny-assistant-fab"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="nanny-assistant-panel"
        aria-haspopup="dialog"
      >
        {open ? 'Close Nanny' : 'Ask Nanny'}
      </button>
    </div>
  );

  return createPortal(ui, document.body);
}

export default NannyAssistant;